import type { MeasurementKind, MeasurementPoint } from '@nutians/shared/schemas';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateMeasurement,
  useDeleteMeasurement,
  useMeasurementSettings,
  useMeasurements,
  useUpdateMeasurementSettings,
} from '../hooks/useMeasurements.js';

const KINDS: MeasurementKind[] = ['body_fat', 'waist', 'hip', 'neck', 'chest', 'arm', 'thigh'];

function defaultUnit(type: MeasurementKind): string {
  return type === 'body_fat' ? '%' : 'cm';
}

function MeasurementForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('measurements');
  const mutation = useCreateMeasurement();
  const [type, setType] = useState<MeasurementKind>('waist');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<string>(defaultUnit('waist'));
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const handleTypeChange = (newType: MeasurementKind) => {
    setType(newType);
    setUnit(defaultUnit(newType));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const num = Number(value);
    if (!num || num <= 0) return;
    mutation.mutate(
      {
        type,
        value: num,
        unit,
        measuredAt: new Date(`${date}T12:00:00Z`).toISOString(),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-husk p-4 flex flex-col gap-3">
      <p className="text-sm font-medium text-ink">{t('add.title')}</p>

      <div className="flex gap-3 flex-wrap">
        <label className="flex flex-col gap-1 flex-1 min-w-[120px]">
          <span className="text-xs text-ink-soft">{t('add.type')}</span>
          <select
            value={type}
            onChange={(e) => handleTypeChange(e.currentTarget.value as MeasurementKind)}
            className="rounded border border-husk px-2 py-1 text-sm text-ink bg-white dark:bg-transparent"
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`types.${k}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 w-28">
          <span className="text-xs text-ink-soft">{t('add.value')}</span>
          <div className="flex items-center gap-1">
            <input
              type="number"
              step="0.1"
              min="0"
              value={value}
              onChange={(e) => setValue(e.currentTarget.value)}
              required
              className="rounded border border-husk px-2 py-1 text-sm text-ink w-full"
            />
            <span className="text-xs text-ink-soft">{unit}</span>
          </div>
        </label>

        <label className="flex flex-col gap-1 flex-1 min-w-[120px]">
          <span className="text-xs text-ink-soft">{t('add.date')}</span>
          <input
            type="date"
            value={date}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.currentTarget.value)}
            className="rounded border border-husk px-2 py-1 text-sm text-ink"
          />
        </label>
      </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 rounded text-sm text-ink-soft hover:text-ink"
        >
          {t('add.cancel')}
        </button>
        <button
          type="submit"
          disabled={mutation.isPending || !value}
          className="px-3 py-1.5 rounded bg-leaf text-white text-sm disabled:opacity-50"
        >
          {t('add.submit')}
        </button>
      </div>
    </form>
  );
}

function TrendLine({ points }: { points: MeasurementPoint[] }) {
  const { t } = useTranslation('measurements');
  const latest = points[points.length - 1];
  if (points.length === 0 || !latest) {
    return <p className="text-sm text-ink-soft py-2">{t('empty')}</p>;
  }
  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-xs text-ink-soft">{t('trend.latest')}</span>
        <span className="text-base font-medium text-ink">
          {latest.value} {latest.unit}
        </span>
        <span className="text-xs text-ink-soft">
          ({t('trend.label')} {latest.trendValue.toFixed(1)} {latest.unit})
        </span>
      </div>
      <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {[...points]
          .reverse()
          .slice(0, 10)
          .map((p) => (
            <PointRow key={p.id} point={p} />
          ))}
      </div>
    </div>
  );
}

function PointRow({ point }: { point: MeasurementPoint }) {
  const del = useDeleteMeasurement();
  const [confirm, setConfirm] = useState(false);
  const { t } = useTranslation('measurements');

  return (
    <div className="flex items-center justify-between py-2 text-sm">
      <span className="text-ink-soft">{point.localDate}</span>
      <span className="text-ink">
        {point.value} {point.unit}
      </span>
      {confirm ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => del.mutate({ id: point.id, type: point.type })}
            disabled={del.isPending}
            className="text-xs text-rose-600 hover:underline"
          >
            {t('delete.confirm')}
          </button>
          <button
            type="button"
            onClick={() => setConfirm(false)}
            className="text-xs text-ink-soft hover:text-ink"
          >
            {t('delete.cancel')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirm(true)}
          className="text-xs text-ink-soft hover:text-ink"
          aria-label={t('delete.confirmTitle')}
        >
          ×
        </button>
      )}
    </div>
  );
}

function KindPanel({ kind }: { kind: MeasurementKind }) {
  const { t } = useTranslation('measurements');
  const { data } = useMeasurements(kind);
  const points = data?.points ?? [];

  return (
    <section>
      <h4 className="text-sm font-medium text-ink mb-2">{t(`types.${kind}`)}</h4>
      <TrendLine points={points} />
    </section>
  );
}

export default function MeasurementsPage() {
  const { t } = useTranslation('measurements');
  const { data: settings, isLoading } = useMeasurementSettings();
  const updateSettings = useUpdateMeasurementSettings();
  const [showForm, setShowForm] = useState(false);

  const enabled = settings?.measurementsEnabled ?? false;

  if (isLoading) return null;

  if (!enabled) {
    return (
      <div className="flex flex-col gap-4 max-w-lg">
        <h2 className="text-xl font-medium tracking-tight">{t('title')}</h2>
        <div className="rounded-lg border border-husk p-6 flex flex-col gap-3">
          <p className="text-sm font-medium text-ink">{t('optIn.heading')}</p>
          <p className="text-sm text-ink-soft">{t('optIn.body')}</p>
          <button
            type="button"
            onClick={() => updateSettings.mutate(true)}
            disabled={updateSettings.isPending}
            className="self-start px-4 py-2 rounded bg-leaf text-white text-sm disabled:opacity-50"
          >
            {t('optIn.enable')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-lg">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium tracking-tight">{t('title')}</h2>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="text-sm text-leaf hover:underline"
        >
          {showForm ? t('add.cancel') : `+ ${t('add.title')}`}
        </button>
      </div>

      {showForm && <MeasurementForm onClose={() => setShowForm(false)} />}

      <div className="flex flex-col gap-6">
        {KINDS.map((k) => (
          <KindPanel key={k} kind={k} />
        ))}
      </div>
    </div>
  );
}
